import MaterialTable from '@material-table/core';
import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from 'src/components/confirmDialog/confirmDialog';
import { InUseDialog } from 'src/components/inUseDialog/inUseDialog';
import { PageAppBar } from 'src/components/pageAppBar';
import { checkSuppressionProfileUsage } from 'src/tools/checkUsage';
import { fetchAllPages } from 'src/tools/graphqlPaging';
import { UserContext } from '../../contexts/UserContext';
import { createAuditLog, deleteSuppression } from '../../graphql/mutations';
import { listSuppressions } from '../../graphql/queries';

export function SuppressionProfiles() {
	const client = generateClient();
	// const classes = useStyles();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [profiles, setProfiles] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [inUseOpen, setInUseOpen] = useState(false);
	const [inUseInfo, setInUseInfo] = useState(null);

	useEffect(() => {
		async function getData() {
			setLoading(true);
			// Paged so all suppression profiles are listed, not just page one.
			const allProfiles = await fetchAllPages({
				client,
				query: listSuppressions,
				resultKey: 'listSuppressions',
				variables: { tenant: userContext.tenantId },
			});
			setProfiles(
				allProfiles.sort(
					(a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
				),
			);
			setLoading(false);
		}
		if (userContext.tenantId) {
			getData();
		}
	}, [userContext.tenantId]);

	async function handleDelete() {
		setLoading(true);

		// Check if profile is in use by a communication profile
		const isInUse = await checkSuppressionProfileUsage(
			toDelete,
			userContext.tenantId,
		);
		if (isInUse.inUse) {
			setInUseInfo({ message: isInUse.message, usedBy: isInUse.usedBy || [] });
			setInUseOpen(true);
			setLoading(false);
			setToDelete(null);
			setConfirmDeleteOpen(false);
			return;
		}

		const deleteResult = await client.graphql({
			query: deleteSuppression,
			variables: { input: { id: toDelete } },
		});
		await client.graphql({
			query: createAuditLog,
			variables: {
				input: {
					tenant: userContext.tenantId,
					type: 'Suppression',
					timestamp: new Date().toISOString(),
					userName: userContext.email,
					documentId: toDelete,
					name: profiles.find((profile) => profile.id === toDelete).name,
					currentValue: '{}',
					action: 'DELETED',
					ttl: Math.floor(Date.now() / 1000) + 6 * 30 * 24 * 60 * 60, //6 months
				},
			},
		});
		console.log(deleteResult);
		//if profile is successful in deleting remove it from the current list.
		if (deleteResult.data) {
			const deletedProfile = deleteResult.data.deleteSuppression;
			const curProfiles = [...profiles];
			curProfiles.splice(
				curProfiles.findIndex((e) => e.id === deletedProfile.id),
				1,
			);
			setProfiles([...curProfiles]);
		}

		setLoading(false);
	}

	function handleCancelDelete() {
		setToDelete(null);
		setConfirmDeleteOpen(false);
	}

	function handleCloseInUse() {
		setInUseOpen(false);
		setInUseInfo(null);
	}

	return (
		<Box>
			<PageAppBar
				title="Suppression Profiles"
				description="Set up suppression profiles"
				actionOneText={
					userContext.securityOptions.suppression > 1 ? '+ Profile' : ''
				}
				actionOneHandler={() => {
					navigate('/suppression/new');
				}}
				actionOneDisabled={userContext.securityOptions.suppression < 2}
			/>
			<MaterialTable
				components={{
					Container: (props) => <Box {...props} elevation={0} />,
				}}
				title=""
				data={profiles}
				columns={[{ title: 'Name', field: 'name' }]}
				isLoading={loading}
				options={{
					actionsColumnIndex: -1,
					pageSize: 10,
					searchFieldStyle: {
						marginBottom: '16px',
						marginleft: '-28px',
					},
					headerStyle: {
						borderTop: '1px solid #e0e0e0',
						padding: '16px',
					},
					searchFieldVariant: 'outlined',
					paginationType: 'stepped',
				}}
				actions={[
					{
						disabled: userContext.securityOptions.suppression < 2,
						icon: () => <EditOutlined color="primary" />,
						tooltip: 'Edit Profile',
						onClick: (event, rowData) => {
							navigate(`/suppression/${rowData.id}`);
						},
					},
					(rowData) =>
						(function () {
							const disabled = userContext.securityOptions.suppression < 2;
							return {
								icon: () => (
									<DeleteOutlineOutlined
										color={disabled ? 'disabled' : 'primary'}
									/>
								),
								tooltip: 'Delete Profile',
								onClick: (event, rowData) => {
									setToDelete(rowData.id);
									setConfirmDeleteOpen(true);
								},
								disabled,
							};
						})(),
				]}
				onRowClick={(event, rowData) => {
					navigate(`/suppression/${rowData.id}`);
				}}
			/>
			<ConfirmDialog
				open={confirmDeleteOpen}
				title="Delete Profile"
				description="Are you sure you want to delete this profile?"
				actionOneText="Delete"
				actionOneHandler={() => {
					handleDelete();
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					handleCancelDelete();
				}}
			/>
			<InUseDialog
				open={inUseOpen}
				title="Suppression Profile In Use"
				message={inUseInfo?.message}
				usedBy={inUseInfo?.usedBy || []}
				onClose={handleCloseInUse}
			/>
		</Box>
	);
}
